import {
  type HTMLAttributes,
  type RefCallback,
  useCallback,
  useLayoutEffect,
  useState,
} from "react";

/** 监听横向滚动容器尺寸，仅在确有溢出时允许键盘停靠。 */
export function useHorizontalOverflow<T extends HTMLElement>(): {
  hasHorizontalOverflow: boolean;
  scrollRegionRef: RefCallback<T>;
} {
  const [element, setElement] = useState<T | null>(null);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const scrollRegionRef = useCallback<RefCallback<T>>((node) => setElement(node), []);

  useLayoutEffect(() => {
    if (!element) return undefined;
    const update = () => {
      setHasHorizontalOverflow(element.scrollWidth > element.clientWidth);
    };
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(element);
    Array.from(element.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [element]);

  return { hasHorizontalOverflow, scrollRegionRef };
}

export type HorizontalScrollRegionProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "tabIndex"
> & {
  "aria-label": string;
};

/** 带可访问名称的横向滚动区；无溢出时从 Tab 顺序移除。 */
export function HorizontalScrollRegion({
  children,
  ...props
}: HorizontalScrollRegionProps) {
  const { hasHorizontalOverflow, scrollRegionRef } = useHorizontalOverflow<HTMLDivElement>();

  return (
    <div
      {...props}
      ref={scrollRegionRef}
      role="region"
      tabIndex={hasHorizontalOverflow ? 0 : -1}
    >
      {children}
    </div>
  );
}
